import { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Checkbox,
    Stack,
    TextField,
    Autocomplete,
    ListItemText,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import QrCodeIcon from '@mui/icons-material/QrCode';
import { format } from 'date-fns';
import DefaultLayout from 'src/layouts/DefaultLayout';
import { db } from '../instant';
import { queryAuthorizedUsersWithPermissions, UserPermission } from '../schema';
import GenerateQRDialog from 'src/components/GenerateQRDialog';
import { usePermissions } from 'src/hooks/usePermissions';
import PermissionDenied from 'src/components/PermissionDenied';
import ConfirmAdminChangeDialog from 'src/components/ConfirmAdminChangeDialog';
import ConfirmAllEventsDialog from 'src/components/ConfirmAllEventsDialog';
import GlobalHeader from 'src/components/GlobalHeader';
import { PERMISSION_LABELS, PERMISSION_KEYS } from 'src/constants/permissions';
import { Link } from 'react-router-dom';
import { processEventSelection } from 'src/utils/eventSelection';

type EventOption =
    | { id: string; name: string; startDate?: number | string | Date; endDate?: number | string | Date }
    | { id: 'all'; name: string };

function getEventOptionLabel(option: EventOption): string {
    if (option.id === 'all') return 'All Events';
    return option.name;
}

function getEventOptionSubtext(option: EventOption): string {
    if (option.id === 'all') return '';
    const e = option as { startDate?: number | string | Date; endDate?: number | string | Date };
    if (e.startDate) {
        const start = new Date(e.startDate);
        if (e.endDate) return `${format(start, 'M/d/yy')} – ${format(new Date(e.endDate), 'M/d/yy')}`;
        return format(start, 'M/d/yy');
    }
    if (e.endDate) return format(new Date(e.endDate), 'M/d/yy');
    return '';
}

type EventAccessRowProps = {
    user: { id: string; name?: string; email?: string; allowedEventIds?: string };
    events: Array<{ id: string; name: string; startDate?: number | string | Date; endDate?: number | string | Date }>;
    selectedEventIds: string[];
    onEventAccessChange: (userId: string, selectedEventIds: string[]) => void;
    disabled: boolean;
};

/** Row with its own Autocomplete state so selecting options only re-renders this row, not the whole table. */
function EventAccessRow({ user, events, selectedEventIds, onEventAccessChange, disabled }: EventAccessRowProps) {
    const allEventsOption: EventOption = { id: 'all', name: 'All Events' };
    const sortedEvents = [...events].sort((a, b) => a.name.localeCompare(b.name));
    const eventOptions: EventOption[] = [allEventsOption, ...sortedEvents];
    const valueFromProps: EventOption[] = selectedEventIds.includes('all')
        ? [allEventsOption]
        : sortedEvents.filter(e => selectedEventIds.includes(e.id));

    const [pendingValue, setPendingValue] = useState<EventOption[] | null>(null);
    const isOpen = pendingValue !== null;
    const displayValue = isOpen ? pendingValue : valueFromProps;

    const handleOpen = () => setPendingValue(valueFromProps);
    const handleClose = (_: React.SyntheticEvent, reason: string) => {
        if (reason === 'selectOption') return;
        if (pendingValue !== null) {
            const ids = pendingValue.some(o => o.id === 'all') ? ['all'] : pendingValue.map(o => o.id);
            const result = processEventSelection(ids, selectedEventIds);
            onEventAccessChange(user.id, result);
        }
        setPendingValue(null);
    };
    const handleChange = (_: React.SyntheticEvent, newValue: EventOption[]) => {
        setPendingValue(newValue);
        // Selecting "All Events" is a single definitive choice — close dropdown and commit
        if (newValue.some(o => o.id === 'all')) {
            const result = processEventSelection(['all'], selectedEventIds);
            onEventAccessChange(user.id, result);
            setPendingValue(null);
        }
    };

    return (
        <TableRow>
            <TableCell
                sx={{
                    minWidth: 100,
                    maxWidth: 180,
                    verticalAlign: 'middle',
                }}
                title={user.name || user.email}
            >
                <Typography
                    component={Link}
                    to={`/user/${user.id}`}
                    variant="body2"
                    noWrap
                    sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        color: 'primary.main',
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' },
                    }}
                >
                    {user.name || user.email}
                </Typography>
            </TableCell>
            <TableCell sx={{ verticalAlign: 'middle', minWidth: 0, width: '100%' }}>
                <Autocomplete<EventOption, true>
                    multiple
                    disableCloseOnSelect
                    open={isOpen}
                    size="small"
                    options={eventOptions}
                    value={displayValue}
                    onChange={handleChange}
                    onOpen={handleOpen}
                    onClose={handleClose}
                    getOptionLabel={getEventOptionLabel}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    renderOption={(props, option) => {
                        const subtext = getEventOptionSubtext(option);
                        return (
                            <li
                                {...props}
                                key={option.id}
                                onMouseDown={e => {
                                    e.preventDefault();
                                    props.onMouseDown?.(e);
                                }}
                            >
                                <ListItemText
                                    primary={option.id === 'all' ? 'All Events' : option.name}
                                    secondary={subtext || undefined}
                                    primaryTypographyProps={{ variant: 'body2' }}
                                    secondaryTypographyProps={{ variant: 'caption' }}
                                />
                            </li>
                        );
                    }}
                    renderInput={params => <TextField {...params} placeholder="Select events" />}
                    disabled={disabled}
                    fullWidth
                    sx={{ width: '100%' }}
                />
            </TableCell>
        </TableRow>
    );
}

export default function UsersScreen() {
    const { canEditUsers, isLoading: permissionsLoading } = usePermissions();
    const { data, isLoading, error } = queryAuthorizedUsersWithPermissions();
    const [expandedPermissions, setExpandedPermissions] = useState(true);
    const [expandedEvents, setExpandedEvents] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [updatingIds, setUpdatingIds] = useState<Set<string>>(new Set());
    const [qrDialogOpen, setQrDialogOpen] = useState(false);
    const [optimisticUpdates, setOptimisticUpdates] = useState<Record<string, Record<string, any>>>({});
    const [optimisticEventUpdates, setOptimisticEventUpdates] = useState<Record<string, string | null>>({});

    // Confirmation dialog states
    const [adminChangeDialog, setAdminChangeDialog] = useState<{
        open: boolean;
        userId: string;
        userName: string;
        isGranting: boolean;
    }>({ open: false, userId: '', userName: '', isGranting: false });

    const [allEventsDialog, setAllEventsDialog] = useState<{
        open: boolean;
        userId: string;
        userName: string;
        selectedEventIds: string[];
    }>({ open: false, userId: '', userName: '', selectedEventIds: [] });

    if (!permissionsLoading && !canEditUsers) {
        return <PermissionDenied requiredPermission="Edit Users" />;
    }

    const users = data?.$users || [];
    const events = data?.events || [];

    const filteredUsers = users
        .filter(user => {
            const searchLower = searchQuery.toLowerCase();
            const nameLower = (user.name || '').toLowerCase();
            const emailLower = (user.email || '').toLowerCase();
            return nameLower.startsWith(searchLower) || emailLower.startsWith(searchLower);
        })
        .sort((a, b) => (a.name || a.email || '').localeCompare(b.name || b.email || ''));

    const handlePermissionChange = async (userId: string, permissionKey: keyof UserPermission, value: boolean) => {
        const user = users.find(u => u.id === userId);
        if (!user) return;

        // Show confirmation dialog for admin permission changes
        if (permissionKey === 'admin') {
            const userName = user.name || user.email;
            setAdminChangeDialog({
                open: true,
                userId,
                userName,
                isGranting: value,
            });
            return;
        }

        // If not admin change, proceed with update
        await performPermissionChange(userId, permissionKey, value);
    };

    const performPermissionChange = async (userId: string, permissionKey: keyof UserPermission, value: boolean) => {
        try {
            const user = users.find(u => u.id === userId);
            if (!user) return;

            // Get current permissions (use optimistic if available)
            const currentPerms = optimisticUpdates[userId] || user || {};

            // Calculate new permissions
            const newPerms = { ...currentPerms };

            // If toggling admin, toggle all other permissions
            if (permissionKey === 'admin') {
                (PERMISSION_KEYS as readonly (keyof UserPermission)[]).forEach(key => {
                    if (key !== 'admin') {
                        newPerms[key] = value;
                    }
                });
            }

            newPerms[permissionKey] = value;

            // Check if all permissions (except admin) are now true
            const allNonAdminPermissionsTrue = PERMISSION_KEYS.filter(key => key !== 'admin').every(key => newPerms[key] === true);

            // If all permissions are true, set admin to true
            // If any permission is false and admin is true, set admin to false
            if (permissionKey !== 'admin') {
                if (allNonAdminPermissionsTrue) {
                    newPerms.admin = true;
                } else if (newPerms.admin === true && value === false) {
                    newPerms.admin = false;
                }
            }

            // Optimistically update UI
            setOptimisticUpdates(prev => ({
                ...prev,
                [userId]: newPerms,
            }));

            // Update user with new permission values
            const userUpdates: Record<string, boolean> = {};
            userUpdates[permissionKey] = value;

            // If toggling admin, update all other permissions too
            if (permissionKey === 'admin') {
                (PERMISSION_KEYS as readonly (keyof UserPermission)[]).forEach(key => {
                    if (key !== 'admin') {
                        userUpdates[key] = value;
                    }
                });
            } else {
                // If toggling other permission, sync admin based on new state
                const allNonAdminTrue = PERMISSION_KEYS.filter(key => key !== 'admin').every(key => {
                    if (key === permissionKey) return value;
                    return (newPerms[key] as boolean) || false;
                });

                if (allNonAdminTrue) {
                    userUpdates.admin = true;
                } else if ((currentPerms.admin as boolean) && !value) {
                    userUpdates.admin = false;
                }
            }

            // Perform transaction
            await db.transact(db.tx.$users[userId].update(userUpdates));

            // Clear optimistic state on success
            setOptimisticUpdates(prev => {
                const next = { ...prev };
                delete next[userId];
                return next;
            });
        } catch (err) {
            console.error('Failed to update permission:', err);
            // Revert optimistic update on failure
            setOptimisticUpdates(prev => {
                const next = { ...prev };
                delete next[userId];
                return next;
            });
        }
    };

    const handleAdminChangeConfirm = async () => {
        const { userId, isGranting } = adminChangeDialog;
        await performPermissionChange(userId, 'admin', isGranting);
    };

    const handleEventAccessChange = async (userId: string, selectedEventIds: string[]) => {
        // Show confirmation dialog only when newly granting "All Events" (user didn't already have it)
        if (selectedEventIds.includes('all')) {
            const current = getSelectedEvents(userId, users.find(u => u.id === userId)?.allowedEventIds);
            if (!current.includes('all')) {
                const user = users.find(u => u.id === userId);
                const userName = user?.name || user?.email || 'this user';
                setAllEventsDialog({
                    open: true,
                    userId,
                    userName,
                    selectedEventIds,
                });
                return;
            }
        }

        await performEventAccessChange(userId, selectedEventIds);
    };

    const performEventAccessChange = async (userId: string, selectedEventIds: string[]) => {
        try {
            setUpdatingIds(prev => new Set(prev).add(userId));

            let eventIdsToStore: string | null;
            if (selectedEventIds.includes('all')) {
                eventIdsToStore = 'all';
            } else if (selectedEventIds.length === 0) {
                eventIdsToStore = null;
            } else {
                eventIdsToStore = JSON.stringify(selectedEventIds);
            }

            // Optimistic update
            setOptimisticEventUpdates(prev => ({
                ...prev,
                [userId]: eventIdsToStore,
            }));

            await db.transact(
                db.tx.$users[userId].update({
                    allowedEventIds: eventIdsToStore,
                })
            );

            // Clear optimistic update on success
            setOptimisticEventUpdates(prev => {
                const next = { ...prev };
                delete next[userId];
                return next;
            });
        } catch (err) {
            console.error('Failed to update event access:', err);
            // Revert optimistic update on failure
            setOptimisticEventUpdates(prev => {
                const next = { ...prev };
                delete next[userId];
                return next;
            });
        } finally {
            setUpdatingIds(prev => {
                const next = new Set(prev);
                next.delete(userId);
                return next;
            });
        }
    };

    const handleAllEventsConfirm = async () => {
        const { userId, selectedEventIds } = allEventsDialog;
        await performEventAccessChange(userId, selectedEventIds);
    };

    const getSelectedEvents = (userId: string, allowedEventIds?: string): string[] => {
        // Check for optimistic update first
        if (userId in optimisticEventUpdates) {
            const optimistic = optimisticEventUpdates[userId];
            if (optimistic === null) return [];
            if (optimistic === 'all') return ['all'];
            try {
                return JSON.parse(optimistic);
            } catch {
                return [];
            }
        }

        if (!allowedEventIds) return [];
        if (allowedEventIds === 'all') return ['all'];
        try {
            return JSON.parse(allowedEventIds);
        } catch {
            return [];
        }
    };

    const getPermissionValue = (userId: string, key: keyof UserPermission): boolean => {
        const optimistic = optimisticUpdates[userId];
        if (optimistic && key in optimistic) {
            return (optimistic[key] as boolean) || false;
        }
        const user = users.find(u => u.id === userId);
        return (user?.[key as keyof UserPermission] as boolean) || false;
    };

    if (isLoading) {
        return <DefaultLayout>Loading...</DefaultLayout>;
    }

    if (error) {
        return <DefaultLayout>Error loading users: {String(error)}</DefaultLayout>;
    }

    return (
        <DefaultLayout>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flexGrow: 1,
                    overflowX: 'hidden',
                    overflowY: 'auto',
                    width: '100%',
                    height: '100%',
                    boxSizing: 'border-box',
                    alignItems: 'center',
                }}
            >
                <GlobalHeader title="Users" />
                <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto', width: '100%', minWidth: 0, boxSizing: 'border-box' }}>
                    <Stack
                        direction="row"
                        sx={{
                            width: '100%',
                            justifyContent: 'center',
                            mb: 2,
                        }}
                    >
                        <Button variant="contained" startIcon={<QrCodeIcon />} onClick={() => setQrDialogOpen(true)}>
                            Invite User
                        </Button>
                    </Stack>

                    <TextField
                        placeholder="Search users..."
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        size="small"
                        sx={{ mb: 2, width: '100%' }}
                    />

                    {/* Permissions Accordion */}
                    <Accordion
                        expanded={expandedPermissions}
                        onChange={() => setExpandedPermissions(!expandedPermissions)}
                        elevation={0}
                        sx={{
                            '&:before': { display: 'none' },
                            borderTop: '1px solid',
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            mb: 2,
                        }}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography fontWeight={600}>Permissions</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 0 }}>
                            <Box sx={{ overflowX: 'auto' }}>
                                <Table size="small" sx={{ width: 'max-content', minWidth: '100%', tableLayout: 'fixed' }}>
                                    <TableHead>
                                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                            <TableCell
                                                sx={{
                                                    fontWeight: 600,
                                                    position: 'sticky',
                                                    left: 0,
                                                    backgroundColor: '#f5f5f5',
                                                    zIndex: 100,
                                                    width: 140,
                                                    minWidth: 140,
                                                    maxWidth: 140,
                                                    boxShadow: '2px 0 4px rgba(0,0,0,0.1)',
                                                }}
                                            >
                                                Name
                                            </TableCell>
                                            {PERMISSION_KEYS.map(key => (
                                                <TableCell
                                                    key={key}
                                                    align="center"
                                                    sx={{
                                                        fontWeight: 600,
                                                        fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.85rem' },
                                                        minWidth: { xs: 50, sm: 70, md: 90 },
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {PERMISSION_LABELS[key]}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredUsers.map(user => (
                                            <TableRow key={user.id}>
                                                <TableCell
                                                    sx={{
                                                        position: 'sticky',
                                                        left: 0,
                                                        backgroundColor: 'white',
                                                        zIndex: 99,
                                                        width: 140,
                                                        minWidth: 140,
                                                        maxWidth: 140,
                                                        boxShadow: '2px 0 4px rgba(0,0,0,0.1)',
                                                        overflow: 'hidden',
                                                    }}
                                                    title={user.name || user.email}
                                                >
                                                    <Box
                                                        sx={{
                                                            minWidth: 0,
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                        }}
                                                    >
                                                        <Typography
                                                            component={Link}
                                                            to={`/user/${user.id}`}
                                                            variant="body2"
                                                            noWrap
                                                            sx={{
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                color: 'primary.main',
                                                                textDecoration: 'none',
                                                                '&:hover': { textDecoration: 'underline' },
                                                            }}
                                                        >
                                                            {user.name || user.email}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                {PERMISSION_KEYS.map(key => (
                                                    <TableCell
                                                        key={`${user.id}-${key}`}
                                                        align="center"
                                                        sx={{ minWidth: { xs: 50, sm: 70, md: 90 } }}
                                                    >
                                                        <Checkbox
                                                            size="small"
                                                            checked={getPermissionValue(user.id, key as keyof UserPermission)}
                                                            onChange={e => {
                                                                handlePermissionChange(
                                                                    user.id,
                                                                    key as keyof UserPermission,
                                                                    e.target.checked
                                                                );
                                                            }}
                                                        />
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Box>
                        </AccordionDetails>
                    </Accordion>

                    {/* Events Accordion */}
                    <Accordion
                        expanded={expandedEvents}
                        onChange={() => setExpandedEvents(!expandedEvents)}
                        elevation={0}
                        sx={{
                            '&:before': { display: 'none' },
                            borderTop: '1px solid',
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                        }}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography fontWeight={600}>Events</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 0 }}>
                            <Box sx={{ overflowX: 'auto' }}>
                                <Table size="small" sx={{ width: '100%' }}>
                                    <TableHead>
                                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                            <TableCell
                                                sx={{
                                                    fontWeight: 600,
                                                    minWidth: 100,
                                                    maxWidth: 180,
                                                }}
                                            >
                                                Name
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Event access</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredUsers.map(user => (
                                            <EventAccessRow
                                                key={user.id}
                                                user={user}
                                                events={events}
                                                selectedEventIds={getSelectedEvents(user.id, user.allowedEventIds)}
                                                onEventAccessChange={handleEventAccessChange}
                                                disabled={updatingIds.has(user.id)}
                                            />
                                        ))}
                                    </TableBody>
                                </Table>
                            </Box>
                        </AccordionDetails>
                    </Accordion>

                    <GenerateQRDialog
                        open={qrDialogOpen}
                        onClose={() => setQrDialogOpen(false)}
                        events={events}
                        permissionLabels={PERMISSION_LABELS}
                    />

                    <ConfirmAdminChangeDialog
                        open={adminChangeDialog.open}
                        onClose={() => setAdminChangeDialog({ ...adminChangeDialog, open: false })}
                        onConfirm={handleAdminChangeConfirm}
                        userName={adminChangeDialog.userName}
                        isGranting={adminChangeDialog.isGranting}
                    />

                    <ConfirmAllEventsDialog
                        open={allEventsDialog.open}
                        onClose={() => setAllEventsDialog({ ...allEventsDialog, open: false })}
                        onConfirm={handleAllEventsConfirm}
                        userName={allEventsDialog.userName}
                    />
                </Box>
            </Box>
        </DefaultLayout>
    );
}
